import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { COGNITO_OAUTH_URL } from 'env';
import { ErrorBox, Spinner, VerticalLayout } from 'widgets';

import { useAuthContext } from './Auth';
import { AuthenticationPage } from './AuthenticationPage';

type LoginLocationState = { loginError?: string } | null;

function buildOAuthUrl(identityProvider: string): string {
    const url = new URL(COGNITO_OAUTH_URL);
    // identity_provider is the name of the Cognito resource
    // without this param, Cognito will show a selector for the user to choose the provider
    url.searchParams.set('identity_provider', identityProvider);
    return url.toString();
}

export const OAuthLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isLoadingUser } = useAuthContext();
    const [isSubmitting, setSubmitting] = useState(false);
    const [submittingProvider, setSubmittingProvider] = useState<string | null>(null);
    const stateLoginError = (location.state as LoginLocationState)?.loginError ?? '';
    const [loginError, setLoginError] = useState(stateLoginError);

    useEffect(() => {
        if (isLoadingUser) {
            return;
        }

        if (!isSubmitting && isAuthenticated) {
            navigate('/home', { replace: true });
            return;
        }
    }, [navigate, isAuthenticated, isLoadingUser, isSubmitting]);

    useEffect(() => {
        if (!stateLoginError) {
            return;
        }

        if (stateLoginError !== loginError) {
            setLoginError(stateLoginError);
        }

        navigate(location.pathname, { replace: true, state: null });
    }, [stateLoginError, loginError, navigate, location.pathname]);

    const handleLogin = (provider: string) => {
        if (isLoadingUser) {
            return;
        }

        setSubmitting(true);
        setSubmittingProvider(provider);
        window.location.replace(buildOAuthUrl(provider));
    };

    if (isLoadingUser) {
        return (
            <AuthenticationPage title="Welcome back, sign in to your account.">
                <VerticalLayout $gap={8} $columnCenter>
                    <div data-testid="oauth-login-loading-spinner">
                        <Spinner size="large" />
                    </div>
                </VerticalLayout>
            </AuthenticationPage>
        );
    }

    return (
        <AuthenticationPage title="Welcome back, sign in to your account.">
            <VerticalLayout $gap={8}>
                <Button
                    variant="highlighted"
                    size="lg"
                    disabled={isSubmitting || isLoadingUser}
                    onClick={() => handleLogin('Google')} // name references Cognito resource
                    data-testid="google-sign-in-button"
                >
                    {submittingProvider === 'Google' ? <Spinner size="small" /> : 'Sign In With Google'}
                </Button>
                <Button
                    variant="highlighted"
                    size="lg"
                    disabled={isSubmitting || isLoadingUser}
                    onClick={() => handleLogin('ClaroEntraID')} // name references Cognito resource
                    data-testid="microsoft-sign-in-button"
                >
                    {submittingProvider === 'ClaroEntraID' ? <Spinner size="small" /> : 'Sign In With Microsoft'}
                </Button>
                {loginError && <ErrorBox>{loginError}</ErrorBox>}
            </VerticalLayout>
        </AuthenticationPage>
    );
};
